import React, { useState,useEffect } from "react";
import { addCliente, updateCliente, deleteCliente } from "../FirestoreDataLoader.tsx"; 
import { AlertTriangle, Edit, Trash2, PlusCircle, ChevronDown } from 'lucide-react';

// Tipo Cliente
type Cliente = {
  Denominazione: string;
  partita_iva: string;
  indirizzo: string;
  cap: string;
  comune: string;
  nazione: string;
};


const ClienteForm: React.FC<{
  onChange: (form: Cliente) => void;
  initial?: Cliente;
}> = ({ onChange, initial }) => {
  const [form, setForm] = useState<Cliente>(
    initial || {
      Denominazione: "",
      partita_iva: "",
      indirizzo: "",
      cap: "",
      comune: "",
      nazione: "",
    }
  );

  const fieldLabels: { [key in keyof Cliente]: string } = {
  Denominazione: "Denominazione",
  partita_iva: "Partita IVA",
  indirizzo: "Indirizzo",
  cap: "CAP",
  comune: "Comune",
  nazione: "Nazione",
};

  const fieldOrder = [
    "Denominazione",
    "partita_iva",
    "indirizzo",
    "cap",
    "comune",
    "nazione",
  ];


  React.useEffect(() => {
    if (initial) {
      setForm(initial);
    }
  }, [initial]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updatedForm = { ...form, [name]: value };
    setForm(updatedForm);
    onChange(updatedForm);
  };
                                                     
  return (
    <div className="form-container">
        {fieldOrder.map((key) => (
        <div className="form-row" key={key}>
          <label className="form-label">{fieldLabels[key as keyof Cliente]}</label>
          <input
            name={key}
            value={form[key as keyof Cliente]}
            onChange={handleChange}
            className="form-input"
          />
        </div>
      ))}
    </div>
  );
};


type ClienteItemProps = {
    cliente: Cliente;
    isExpanded: boolean;
    onClick: (denominazione: string) => void;
    onEdit: (cliente: Cliente) => void;
    onDelete: (denominazione: string) => void;
};


const ClienteItem = React.forwardRef<HTMLDivElement, ClienteItemProps>(
    ({ cliente, isExpanded, onClick, onEdit, onDelete }, ref) => {
    
    const displayDetails = (c: Cliente) => {
      const { Denominazione, ...details } = c;
      return Object.entries(details).map(([key, value]) => {
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        return (
          <p key={key} style={{ margin: '0.2rem 0' }}>
            <strong>{label}:</strong> {value}
          </p>
        );
      });
    };

    return (
      <div
        ref={ref}
        style={{
          border: '1px solid #ccc',
          borderRadius: '4px',
          marginBottom: '0.5rem',
          padding: '0.5rem 1rem',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          backgroundColor:'#fff',
          boxShadow: isExpanded ? '0 2px 5px rgba(0,0,0,0.1)' : 'none',
        }}
        onClick={() => onClick(cliente.Denominazione)}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{width:'80%',  overflowWrap: 'anywhere'}}>{cliente.Denominazione}</h3>
          <ChevronDown style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} size={20} />
        </div>
        {isExpanded && (
          <div style={{ paddingTop: '0.5rem', borderTop: '1px dotted #ccc', marginTop: '0.5rem', overflowWrap: 'anywhere' }}>
            {displayDetails(cliente)}
            <div style={{ marginTop: '1rem', display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
              <button onClick={(e) => { e.stopPropagation(); onEdit(cliente); }} className="button-style" style={{ maxWidth:'8rem', fontSize:13 }}>
                <Edit size={16} style={{marginRight:8,}} /> Modifica
              </button>
              <button onClick={(e) => { e.stopPropagation(); onDelete(cliente.Denominazione); }} className="button-styleX" style={{ backgroundColor: '#c73037ff', maxWidth:'8rem', fontSize:13 }}>
                <Trash2 size={16} style={{marginRight:8}}/> Elimina
              </button>
            </div>
          </div>
        )}
      </div>
    );
});


// Componente principale AnagraficApp
const AnagraficApp: React.FC<{
  onClose: () => void;
  anagraficaClienti: Cliente[];
  updateAnagraficaClienti: (data:Cliente[]) => void;
}> = ({ onClose, anagraficaClienti, updateAnagraficaClienti }) => {
  
  const [view, setView] = useState<"list" | "aggiungi" | "modifica" | "elimina">("list");

  const expandedItemRef = React.useRef<HTMLDivElement>(null);

  // Solo clickedClient gestisce l'espansione.
  const [clickedClient, setClickedClient] = useState<string>(""); 
  const [clienteInLavorazione, setClienteInLavorazione] = useState<Cliente | null>(null);
  const [originalCliente, setOriginalCliente] = useState<Cliente | null>(null);

  const salvaClienti = (data:Cliente[]) => {
    updateAnagraficaClienti(data);
  };

  //const expandedClient = clickedClient; 

  //const clienteSelezionato = anagraficaClienti.find((c) => c.Denominazione === expandedClient) || null;

  const tuttiCampiCompilati = (cliente: Cliente | null) => {
    if (!cliente) return false;
    return Object.values(cliente).every((val) => val.trim() !== "");
  };

  //  Gestione Funzioni CRUD e Viste 

  const handleIniziaAggiungi = () => {
    setClienteInLavorazione(null);
    setView("aggiungi");
    setClickedClient(""); // Chiude qualsiasi espansione
  };
  
  const handleAddCliente = async () => {
    if (clienteInLavorazione && tuttiCampiCompilati(clienteInLavorazione)) {
      await addCliente(clienteInLavorazione);
      const isDuplicate = anagraficaClienti.some(c => c.partita_iva === clienteInLavorazione.partita_iva || c.Denominazione === clienteInLavorazione.Denominazione);
      if (!isDuplicate) {
        salvaClienti([...anagraficaClienti, clienteInLavorazione]);
      }
      setClienteInLavorazione(null);
      setView("list");
    }
  };

  const handleIniziaModifica = (cliente: Cliente) => {
    setOriginalCliente(cliente);
    setClienteInLavorazione(cliente);
    setView("modifica");
    setClickedClient(""); // Chiude qualsiasi espansione
  };
  
  const handleUpdateCliente = async () => {
    if (clienteInLavorazione && tuttiCampiCompilati(clienteInLavorazione)) {
      await updateCliente(clienteInLavorazione);
      const updated = anagraficaClienti.map((c) => 
        (c.partita_iva === clienteInLavorazione.partita_iva ? clienteInLavorazione : c)
      );
      salvaClienti(updated);
      setView("list");
      setClienteInLavorazione(null);
      setOriginalCliente(null);
    }
  };

  const handleIniziaElimina = (denominazione: string) => {
    setClickedClient(denominazione);
    setView("elimina");
  };

  const handleDeleteCliente = async () => {
    const clienteToDelete = anagraficaClienti.find((c) => c.Denominazione === clickedClient);
    if (clienteToDelete) {
      await deleteCliente(clienteToDelete);
      const updated = anagraficaClienti.filter((c) => c.Denominazione !== clickedClient);
      salvaClienti(updated);
      setClickedClient("");
      setView("list");
    }
  };

  const handleBackToList = () => {
    setView("list");
    setClienteInLavorazione(null);
    setOriginalCliente(null);
    setClickedClient(""); 
  };

  // Funzione che gestisce l'apertura/chiusura del div al click
  const handleToggleClick = (denominazione: string) => {
    setClickedClient(denominazione === clickedClient ? "" : denominazione);
    if (view !== "list") {
        setView("list");
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (clickedClient && expandedItemRef.current) {
        expandedItemRef.current.scrollIntoView({
          behavior: 'smooth', 
          block: 'start',//'nearest', 
        });
      }
    }, 100); 

    return () => clearTimeout(timer); 
  }, [clickedClient]);


  const currentTitle = {
    list: "Lista Clienti",
    aggiungi: "Aggiungi Nuovo Cliente",
    modifica: `Modifica: ${originalCliente?.Denominazione || ""}`,
    elimina: `Elimina: ${clickedClient || ""}`,
  }[view];


  return (
    <div className="modal-overlay">
      <div style={{display: 'flex', backgroundColor:'#fff', flexDirection: 'column', minWidth:'17rem', width:'30%', maxWidth:'40%', minHeight:'40%', maxHeight:'70%', padding:'0 0 2rem 2rem' , borderRadius:'4px', fontFamily: 'Segoe UI'}}>
        <div style={{display:'flex', justifyContent:'flex-end', width:'100%'}}>
          <button onClick={onClose} className="Xclose" style={{border:0, borderTopRightRadius:'4px', width:'40px', height:'40px'}}>✕</button>
        </div>
        <div className="modal-header" style={{ marginRight:'2rem', marginBottom:'1rem', borderBottom:'1px solid #ccc', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <h2 className="titlefont" style={{minWidth:'40%', overflowWrap: 'anywhere'}}>
            {currentTitle}
          </h2>
          {view === "list" && (
            <button onClick={handleIniziaAggiungi} className="button-style" style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
              <PlusCircle size={18} /> Aggiungi
            </button>
          )}
        </div>
        <div style={{display:'flex', marginRight:'2rem', overflowY: 'auto'}}>
          
          {/* Vista Lista Clienti (visibile in 'list' e 'elimina') */}
          {(view === "list" || view === "elimina") && (
            <div className="list-wrapper">
              {anagraficaClienti.length > 0 ? (
                anagraficaClienti.map((cliente) => (
                  <ClienteItem
                    key={cliente.partita_iva}
                    cliente={cliente}
                    isExpanded={cliente.Denominazione === clickedClient}
                    onClick={handleToggleClick} 
                    ref={cliente.Denominazione === clickedClient ? expandedItemRef : null}
                    onEdit={handleIniziaModifica}
                    onDelete={handleIniziaElimina}
                  />
                ))
              ) : (
                <p>Nessun cliente in anagrafica.</p>
              )}
            </div>
          )}

          {/* Vista Aggiungi Cliente */}
          {view === "aggiungi" && (
            <div style={{flex: 1 , width: '100%' , height: '100%'}}>
              <div style={{ flex: 1, padding: '0.8rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', overflowY: 'auto', backgroundColor: '#f9fafb', height: '100%' }}>
                <ClienteForm onChange={(form) => setClienteInLavorazione(form)} />
              </div>
              <div className="button-menu" style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1rem'}}>
                <button
                  onClick={handleAddCliente}
                  className="button-style"
                  disabled={!tuttiCampiCompilati(clienteInLavorazione)}
                >
                  Conferma
                </button>
                <button onClick={handleBackToList} className="button-style">Annulla</button>
              </div>
            </div>
          )}

          {/* Vista Modifica Cliente */}
          {view === "modifica" && originalCliente && (
            <div style={{flex: 1 , width: '100%' , height: '100%'}}>
              <div style={{ flex: 1, padding: '0.8rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', overflowY: 'auto', backgroundColor: '#f9fafb', height: '100%' }}>
                <ClienteForm 
                  initial={originalCliente} 
                  onChange={(form) => setClienteInLavorazione(form)} 
                />
              </div>
              <div className="button-menu" style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1rem'}}>
                <button
                  onClick={handleUpdateCliente}
                  className="button-style"
                  disabled={!tuttiCampiCompilati(clienteInLavorazione)}
                >
                  Conferma
                </button>
                <button onClick={handleBackToList} className="button-style">Annulla</button>
              </div>
            </div>
          )}

          {/* Vista Elimina Cliente (Conferma) */}
          {view === "elimina" && clickedClient && (
            <div className="modal-overlay">
              <div className="modal-content" style={{ borderRadius:'6px', padding:'1rem', width:'28%', backgroundColor:'#ffe6e6ff'}}>
                <div style={{marginBottom:'1rem'}} >
                  <p style={{display:"flex", flexDirection:'row', color:'#cf3a3aff', fontFamily:'Segoe UI', alignItems: 'center'}}>
                    <AlertTriangle style={{ width:'2.5rem', height:'2.5rem', minWidth: '2.5rem' }} />
                    <p style={{ paddingLeft:'1.5rem', fontSize:16, overflowWrap: 'anywhere' }}>
                      Stai per eliminare il cliente <strong>{clickedClient}</strong>.<br/>
                      <strong>Attenzione:</strong> Questa azione è <strong>irreversibile</strong>.
                    </p>
                  </p>
                </div>
                <div className="button-menu" style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1rem', paddingRight: '2rem'}}>
                  <button
                    onClick={handleDeleteCliente}
                    className="button-styleX"
                    style={{backgroundColor:'#cf3a3aff', maxWidth:'13rem', fontSize:14, height:'2rem'}}
                    disabled={!clickedClient}
                  >
                    Conferma Eliminazione
                  </button>
                  <button className="button-style" style={{maxWidth:'13rem', fontSize:14, height:'2rem'}} onClick={handleBackToList}>Annulla</button>
                </div>
              </div>
            </div>
          )}
        </div>
       
      </div>
    </div>
  );
};

export default AnagraficApp;